/**
 * Validation Service
 * 
 * Provides flexible validation with language-specific rules and logic.
 * Supports different field requirements per language/country.
 */

export interface ValidationContext {
  language: string;
  country?: string;
  fieldName: string;
  allValues?: Record<string, unknown>;
}

export interface ValidationRule {
  validate: (value: unknown, context: ValidationContext) => boolean;
  errorMessage: string | ((context: ValidationContext) => string);
}

export interface FieldValidationRules {
  [fieldName: string]: ValidationRule[];
}

export interface ValidationRuleset {
  language: string;
  rules: FieldValidationRules;
}

export interface ValidationError {
  field: string;
  message: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/**
 * Validates a single field against its rules
 */
export function validateField(
  fieldName: string,
  value: unknown,
  rules: ValidationRule[],
  context: ValidationContext
): ValidationError | null {
  for (const rule of rules) {
    if (!rule.validate(value, context)) {
      const message =
        typeof rule.errorMessage === 'function'
          ? rule.errorMessage(context)
          : rule.errorMessage;
      
      return {
        field: fieldName,
        message,
      };
    }
  }
  
  return null;
}

/**
 * Validates all fields in a form against a ruleset
 */
export function validateForm(
  data: Record<string, unknown>,
  ruleset: ValidationRuleset,
  country?: string
): ValidationResult {
  const errors: ValidationError[] = [];
  
  const context: ValidationContext = {
    language: ruleset.language,
    country,
    fieldName: '',
    allValues: data,
  };
  
  // Validate each field that has rules
  for (const [fieldName, rules] of Object.entries(ruleset.rules)) {
    context.fieldName = fieldName;
    const value = data[fieldName];
    
    const error = validateField(fieldName, value, rules, context);
    if (error) {
      errors.push(error);
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Common validation functions that can be reused across rulesets
 */
export const commonValidators = {
  required: (value: unknown): boolean => {
    if (typeof value === 'string') {
      return value.trim().length > 0;
    }
    return value !== null && value !== undefined && value !== '';
  },
  
  minLength: (min: number) => (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return value.trim().length >= min;
  },
  
  maxLength: (max: number) => (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return value.trim().length <= max;
  },
  
  email: (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    // RFC 5322 simplified email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value.trim());
  },
  
  latinCharacters: (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    // Basic Latin + common accents + hyphen + apostrophe + space
    const latinRegex = /^[a-zA-Z\s'-]+$/;
    return latinRegex.test(value);
  },
  
  latinExtended: (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    // Extended Latin (includes accented characters for European languages)
    const latinExtendedRegex = /^[a-zA-ZÀ-ÿ\u0100-\u017F\s'-]+$/;
    return latinExtendedRegex.test(value);
  },
  
  alphanumeric: (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    const alphanumericRegex = /^[a-zA-Z0-9\s]+$/;
    return alphanumericRegex.test(value);
  },
  
  pattern: (regex: RegExp) => (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return regex.test(value);
  },
  
  oneOf: (options: string[]) => (value: unknown): boolean => {
    if (typeof value !== 'string') return false;
    return options.includes(value);
  },
  
  custom: (fn: (value: unknown, context: ValidationContext) => boolean) => fn,
};

/**
 * Merge multiple validation results
 */
export function mergeValidationResults(
  results: ValidationResult[]
): ValidationResult {
  const allErrors = results.flatMap((r) => r.errors);
  
  return {
    valid: allErrors.length === 0,
    errors: allErrors,
  };
}

/**
 * Get validation error messages as a Record<field, message>
 */
export function getErrorMessages(
  result: ValidationResult
): Record<string, string> {
  const messages: Record<string, string> = {};
  
  for (const error of result.errors) {
    // Use the first error for each field
    if (!messages[error.field]) {
      messages[error.field] = error.message;
    }
  }
  
  return messages;
}

